<template>
  <div
    role="main"
    aria-label="Modification d’une localité logistique"
    tabindex="-1"
  >
    <b-card
      title="Modifier une localité"
      aria-label="Carte de modification de localité"
    >
      <p class="sr-only">
        Veuillez modifier les informations de la localité en respectant la terminologie logistique professionnelle. Tous les champs marqués comme obligatoires doivent être renseignés.
      </p>
      <div
        v-if="isLoading"
        class="text-center my-3"
        aria-live="polite"
      >
        <b-spinner variant="primary" />
        <p class="mt-2">
          Chargement des données...
        </p>
      </div>

      <b-form
        v-else
        aria-label="Formulaire de modification de localité"
        role="form"
        @submit.prevent="submitForm"
      >
        <b-row>
          <b-col md="12">
            <b-form-group
              label="Code géographique"
              label-for="code"
            >
              <b-form-input
                id="code"
                v-model="form.code"
                readonly
                plaintext
                class="form-control-plaintext font-weight-bold"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label-for="name"
              description="Saisissez le nom officiel de la localité tel qu’utilisé dans la logistique."
            >
              <template #label>
                Nom de la localité <span class="text-danger">*</span>
              </template>
              <b-form-input
                id="name"
                v-model="form.name"
                :state="errors.name ? false : null"
                placeholder="Nom de la localité (ex : Centre logistique Nord)"
                required
                aria-required="true"
                aria-describedby="name-desc"
              />
              <small
                id="name-desc"
                class="form-text text-muted"
              >Nom unique utilisé dans la chaîne logistique.</small>
              <b-form-invalid-feedback>
                {{ errors.name || 'Le nom de la localité est requis.' }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label-for="zip_code"
              description="Indiquez le code postal officiel de la localité."
            >
              <template #label>
                Code postal <span class="text-danger">*</span>
              </template>
              <b-form-input
                id="zip_code"
                v-model="form.zip_code"
                :state="errors.zip_code ? false : null"
                placeholder="Code postal (ex : 1000)"
                type="text"
                required
                aria-required="true"
                aria-describedby="zip-desc"
              />
              <small
                id="zip-desc"
                class="form-text text-muted"
              >Code postal utilisé pour l’acheminement logistique.</small>
              <b-form-invalid-feedback>
                {{ errors.zip_code || 'Le code postal est requis.' }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label-for="governorate"
              description="Sélectionnez le gouvernorat auquel appartient la localité."
            >
              <template #label>
                Gouvernorat <span class="text-danger">*</span>
              </template>
              <v-select
                id="governorate"
                v-model="selectedGovernorate"
                :options="governorateOptions"
                placeholder="Sélectionner un gouvernorat"
                :clearable="false"
                :searchable="true"
                label="name"
                :state="errors.governorate ? false : null"
                aria-required="true"
                aria-describedby="gov-desc"
                @input="onGovernorateChange"
              />
              <small
                id="gov-desc"
                class="form-text text-muted"
              >Le gouvernorat est une division administrative majeure dans la logistique territoriale.</small>
              <b-form-invalid-feedback>
                {{ errors.governorate || 'Le gouvernorat est requis.' }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label-for="delegation"
              description="Sélectionnez la délégation logistique correspondante."
            >
              <template #label>
                Délégation <span class="text-danger">*</span>
              </template>
              <v-select
                id="delegation"
                v-model="form.delegation"
                :options="filteredDelegations"
                placeholder="Sélectionner une délégation"
                :clearable="false"
                :searchable="true"
                label="name"
                :state="errors.delegation ? false : null"
                :disabled="!selectedGovernorate"
                required
                aria-required="true"
                aria-describedby="del-desc"
              />
              <small
                id="del-desc"
                class="form-text text-muted"
              >La délégation est une subdivision logistique du gouvernorat.</small>
              <b-form-invalid-feedback>
                {{ errors.delegation || 'La délégation est requise.' }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            md="6"
            sm="12"
          >
            <b-form-group
              label="Statut"
              label-for="is_active"
            >
              <b-form-checkbox
                id="is_active"
                v-model="form.is_active"
                name="is_active"
                switch
                size="lg"
              >
                {{ form.is_active ? 'Actif' : 'Inactif' }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="mt-2">
          <b-col>
            <b-button
              variant="primary"
              type="submit"
              :disabled="isSaving"
              class="mr-2"
              aria-label="Valider la modification de la localité"
            >
              <span v-if="isSaving">
                <b-spinner small />
                Sauvegarde...
              </span>
              <span v-else>Sauvegarder</span>
            </b-button>
            <b-button
              variant="outline-secondary"
              :to="{ name: 'localities-list' }"
              aria-label="Annuler et retourner à la liste des localités"
            >
              Annuler
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>
  </div>
</template>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  border: 0;
}
</style>

<script>
import {
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BFormCheckbox,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BFormCheckbox,
    vSelect,
  },
  data() {
    return {
      form: {
        name: '',
        code: '',
        zip_code: '',
        delegation: null, // will hold delegation object
        is_active: true,
      },
      errors: {},
      isLoading: true,
      isSaving: false,
      governorateOptions: [],
      delegationOptions: [],
      filteredDelegations: [],
      selectedGovernorate: null,
      localityId: this.$route.params.id,
    }
  },
  created() {
    this.fetchGovernorateOptions()
    this.fetchDelegationOptions()
    this.fetchLocalityData()
  },
  methods: {
    async fetchGovernorateOptions() {
      try {
        const { data } = await axios.get('/api/governorates/')
        this.governorateOptions = data
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des gouvernorats')
      }
    },

    async fetchDelegationOptions(governorateId = null) {
      try {
        let url = '/api/delegations/'
        if (governorateId) {
          url += `?governorate=${governorateId}`
        }
        const { data } = await axios.get(url)
        this.delegationOptions = data
        this.filteredDelegations = data
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des délégations')
      }
    },

    async fetchLocalityData() {
      try {
        const { data } = await axios.get(`/api/localities/detail/${this.localityId}/`)
        this.form = {
          name: data.name,
          code: data.code || '',
          zip_code: data.zip_code || '',
          delegation: null, // will be set below
          is_active: data.is_active,
        }

        // Set selected governorate
        if (data.governorate) {
          this.selectedGovernorate = this.governorateOptions.find(g => g.id === data.governorate.id)
          // Fetch delegations for this governorate
          await this.fetchDelegationOptions(data.governorate.id)
          // Set delegation object
          if (data.delegation) {
            this.form.delegation = this.delegationOptions.find(d => d.id === data.delegation.id) || null
          }
        }
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement de la localité')
      } finally {
        this.isLoading = false
      }
    },

    async onGovernorateChange(governorate) {
      if (governorate) {
        await this.fetchDelegationOptions(governorate.id)
        this.form.delegation = null
      } else {
        this.filteredDelegations = []
        this.form.delegation = null
      }
    },

    async submitForm() {
      this.isSaving = true
      this.errors = {}

      // Prepare payload: send delegation ID, not object
      const payload = {
        ...this.form,
        delegation: this.form.delegation ? this.form.delegation.id : null,
      }

      try {
        await axios.put(`/api/localities/update/${this.localityId}/`, payload)
        this.showToast('success', 'top-center', 'Localité modifiée avec succès')
        this.$router.push({ name: 'localities-list' })
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors = error.response.data
        }
        this.showToast('danger', 'top-center', 'Erreur lors de la modification de la localité')
      } finally {
        this.isSaving = false
      }
    },

    showToast(variant, position, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: 'InfoIcon',
          text,
          variant,
        },
      }, {
        position,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
